<template>
  <div>
    <p>{{ product ? product.productname : 'Product Not Selected' }}</p>

    <loading-container v-if="loading" />
    <div v-else>
      <b-button
        variant="outline-primary"
        class="mb-1"
        :disabled="saveLoading || portfolioLimitReached"
        @click="showCreatePortfolio = true"
      >
        Create New Portfolio
        <feather-icon
          v-if="portfolioLimitReached"
          icon="LockIcon"
          class="ml-50"
        />
      </b-button>
      <p
        v-if="portfolioLimitReached"
        class="text-muted"
      >
        You have reached your limit of having {{ portfolioMax }} portfolios
      </p>

      <b-form-group
        class="mb-0"
        label="Portfolios"
      >
        <div
          v-for="option in mappedPortfolio"
          :key="option.portfolioId"
        >
          <span
            :id="`portfolio-checkbox-wrapper-${option.portfolioId}`"
            class="d-inline-block"
            tabindex="0"
          >
            <b-form-checkbox
              v-model="option.in"
              :value="Boolean(option.portfolioId)"
              name="flavour-3a"
              :disabled="portfolioProductLimitReached(option)"
              :class="{ 'portfolio-checkbox-locked': portfolioProductLimitReached(option) }"
            >
              <feather-icon
                v-if="portfolioProductLimitReached(option)"
                icon="LockIcon"
                size="11"
                class="portfolio-checkbox-lock"
              />

              {{ option.portfolioName }}
            </b-form-checkbox>
          </span>

          <b-tooltip
            v-if="portfolioProductLimitReached(option)"
            :target="`portfolio-checkbox-wrapper-${option.portfolioId}`"
            triggers="hover"
            placement="top"
          >
            This portfolio has reached the maximum limit of
            {{ maxNumberOfProductsInPortfolio }} products.
          </b-tooltip>
        </div>
      </b-form-group>

      <div class="text-center mt-1">
        <b-button
          variant="primary"
          class="mt-1"
          :disabled="saveLoading"
          @click.prevent="updateProductPortfolio"
        >
          SAVE CHANGES <b-spinner
            v-if="saveLoading"
            small
          />
        </b-button>
      </div>
    </div>

    <b-modal
      v-model="showCreatePortfolio"
      centered
      title="Create New Portfolio"
      hide-footer
    >
      <p v-if="portfolioLimitReached">
        You have reached your limit of having {{ portfolioMax }} portfolios
      </p>
      <p v-else>Create a portfolio, then use Save Changes to add this product to your selected portfolios.</p>
      <create-portfolio-modal-content
        v-if="!portfolioLimitReached"
        :portfolios="mappedPortfolio"
        @portfolio-created="onPortfolioCreated"
      />
    </b-modal>
  </div>
</template>
<script>
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import CreatePortfolioModalContent from '@/views/components/CreatePortfolioModalContent.vue'
import {
  BFormGroup,
  BFormCheckbox,
  BButton,
  BSpinner,
  BModal,
  BTooltip,
} from 'bootstrap-vue'
import strings from '@/strings'
import { mapGetters } from 'vuex'

export default {
  components: {
    LoadingContainer,
    CreatePortfolioModalContent,
    BFormGroup,
    BFormCheckbox,
    BButton,
    BSpinner,
    BModal,
    BTooltip,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saveLoading: false,
      showCreatePortfolio: false,
      portfolios: [],
      in: [],
      out: [],
    }
  },
  computed: {
    ...mapGetters('auth', [
      'portfolioMax',
      'portfolioMaxProducts',
    ]),
    portfolioLimitReached() {
      const count = Math.max(this.storePortfolios.length, this.portfolios.length)
      return this.portfolioMax !== 0 && count >= this.portfolioMax
    },
    portfolioIn() {
      return this.portfolios.filter(portfolio => portfolio.in !== false)
    },
    portfolioNotIn() {
      return this.portfolios.filter(portfolio => portfolio.in === false)
    },
    storePortfolios() {
      return this.$store.state.app.portfolios
    },
    maxNumberOfProductsInPortfolio() {
      return this.portfolioMaxProducts
    },
    mappedPortfolio() {
      return this.portfolios.map(portfolio => {
        // Get matching portfolio from the store
        const storePortfolio = this.storePortfolios.find(p => p.portfolioId === portfolio.portfolioId) || portfolio

        const newPortfolio = portfolio // { ...portfolio }
        newPortfolio.portfolioName = storePortfolio.portfolioName
        newPortfolio.productCount = storePortfolio.productCount
        return newPortfolio
      })
    },
  },
  mounted() {
    this.loadPortfoliosByProduct(this.product.productid)
  },
  methods: {
    onPortfolioCreated({ portfolioId, portfolioName }) {
      this.portfolios.push({
        portfolioId,
        portfolioName,
        productCount: 0,
        in: true,
      })
      this.showCreatePortfolio = false
      this.$store.dispatch('app/updatePortfolios')
      this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)
      this.$bvToast.toast('Portfolio created and selected. Click Save Changes to add this product.', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })
    },
    updateProductPortfolio() {
      this.saveLoading = true

      const portfolioIds = this.portfolioIn.map(p => p.portfolioId)

      this.$http.post('lifecycle/public/portfolio.cfc?method=replacePortfoliosByProduct', new URLSearchParams({
        productId: this.product.productid,
        portfolioIds,
      }))
        .then(response => {
          // Emit event to indicate portfolio update
          this.$emit('portfolioUpdated', response.data.addPortfolioIds.concat(response.data.removePortfolioIds))
        })
        .catch(() => {
          this.$bvToast.toast(strings.defaultErrorMessage, {
            title: strings.defaultErrorTitle,
            variant: 'danger',
            solid: true,
          })
        })
        .then(() => {
          this.saveLoading = false
        })
    },
    loadPortfoliosByProduct(id) {
      this.loading = true

      this.$http.get(`lifecycle/public/portfolio.cfc?method=getPortfoliosByProduct&productId=${id}`)
        .then(response => {
          // Sort by portfolios `in` property
          const productPortfolios = response.data.portfolios
          productPortfolios.sort((a, b) => {
            if (a.in && !b.in) {
              return -1
            }
            return 1
          })
          this.portfolios = productPortfolios
        }).catch(() => {
          this.$bvToast.toast('Fetching Portfolios Failed', {
            title: strings.defaultErrorTitle,
            variant: 'danger',
            solid: true,
          })
        }).then(() => {
          this.loading = false
        })
    },
    portfolioProductLimitReached(portfolio) {
      return this.maxNumberOfProductsInPortfolio > 0
        && portfolio.productCount >= this.maxNumberOfProductsInPortfolio
        && !portfolio.in
    },
  },
}
</script>

<style scoped>
.portfolio-checkbox-locked {
  position: relative;
}

.portfolio-checkbox-lock {
  position: absolute;
  left: 3px;
  top: 5px;
  z-index: 5;
  pointer-events: none;
}
</style>
